import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchableDropdown.module.css';
import NavigationList from './search.json';
import icon from './icons/search.svg';

const SearchableDropdown = ({ navList = [] }) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedItem, setSelectedItem] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const itemRefs = useRef([]);

  const normalize = (str) => str.toLowerCase().replace(/\s+/g, '').trim();
  const normalizePath = (path) => (path.startsWith('/') ? path : `/${path}`);

  const permittedPaths = useMemo(() => {
    const paths = new Set();
    navList.forEach((group) => {
      group.items?.forEach((item) => {
        if (!item?.url) return;
        let path = item.url;
        if (path.startsWith('/app/settings')) {
          path = path.replace('/app/settings', '');
        }
        paths.add(normalizePath(path));
      });
    });
    return paths;
  }, [navList]);

  const filtered = NavigationList.filter((item) => {
    const path = normalizePath(item.path || '');
    if (permittedPaths.size > 0 && !permittedPaths.has(path)) {
      return false;
    }
    return (
      normalize(item.label).includes(normalize(search)) ||
      normalize(search).includes(normalize(item.label))
    );
  });

  const handleSelect = (item) => {
    setSearch(item.label);
    setSelectedItem(item);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (selectedItem) {
      navigate(`/app/settings${selectedItem.path}`);
    }
  }, [selectedItem, navigate]);

  const handleKeyDown = (e) => {
    if (!showDropdown || filtered.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filtered.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + filtered.length) % filtered.length,
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          handleSelect(filtered[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  return (
    <div className={styles.dropdownContainer}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <img src={icon} alt='Search' />
        <input
          ref={inputRef}
          type='text'
          className={styles.searchBox}
          placeholder='Search Settings'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
          }}
          onBlur={(e) => {
            if (
              !e.relatedTarget ||
              !e.currentTarget.contains(e.relatedTarget)
            ) {
              setShowDropdown(false);
            }
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {showDropdown && (
        <div className={styles.dropdown}>
          {filtered.length > 0 ? (
            filtered.map((item, index) => (
              <div
                key={item.label}
                ref={(el) => (itemRefs.current[index] = el)}
                tabIndex={-1} //  -1 makes it focusable for blur handling
                className={`${styles.dropdownItem} ${
                  index === highlightedIndex ? styles.highlighted : ''
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
              >
                {item.label}
              </div>
            ))
          ) : (
            <div className={styles.noResults}>
              <p>We couldn't find anything matching your search.</p>
              <p>Try again with a different term.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
